import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Download, CheckCircle, Phone, Filter, Mail, Settings, Minimize2, AlertTriangle } from 'lucide-react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import SEOHead from '../components/SEOHead';
import Footer from '../components/Footer';
import FiltrationCTABanner from '../components/FiltrationCTABanner';

const TIGSeriesPage = () => {
  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        title="TIG True Inline Gasketed Filters | CTC Waterworks"
        description="TIG Series true inline gasketed filters for specific layouts and tie-ins. Manual cleaning with serviceable gaskets and compatible screen options for drip, micro-sprinkler, and polishing applications."
        canonical="https://www.ctcwaterworks.com/filtration/inline/tig"
      />
      <Navbar />
      <FiltrationCTABanner />

      {/* Breadcrumbs */}
      <nav aria-label="Breadcrumb" className="bg-gray-50 border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 py-3">
          <ol className="flex items-center space-x-2 text-sm">
            <li>
              <Link to="/" className="text-gray-600 hover:text-ctc-blue">Home</Link>
            </li>
            <li className="text-gray-400">/</li>
            <li>
              <Link to="/filtration" className="text-gray-600 hover:text-ctc-blue">Filtration</Link>
            </li>
            <li className="text-gray-400">/</li>
            <li>
              <Link to="/filtration/inline" className="text-gray-600 hover:text-ctc-blue">Inline</Link>
            </li>
            <li className="text-gray-400">/</li>
            <li className="text-gray-900 font-semibold">TIG Series</li>
          </ol>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-16 pb-12 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Main Content - Left 2/3 */}
            <div className="lg:col-span-2">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
              >
                <div className="flex items-center mb-4">
                  <Minimize2 className="text-gray-700 mr-4" size={48} />
                  <div>
                    <h1 className="text-5xl font-bold text-gray-900">
                      TIG—True Inline Gasketed Filters
                    </h1>
                  </div>
                </div>
                <p className="text-2xl text-gray-600 mb-8 font-light leading-relaxed">
                  Compact inline design with quick-access end caps for manual cleaning. Ideal for space-constrained installations, municipal pre-treatment, industrial process water, and applications where true-inline flow path is required.
                </p>

                <div className="bg-white/60 backdrop-blur-sm border border-gray-200 rounded-lg p-6 mb-6">
                  <h3 className="font-bold text-lg mb-3 text-gray-900">TIG = True Inline Gasketed</h3>
                  <p className="text-gray-700 leading-relaxed">
                    The TIG Series offers true inline geometry where inlet and outlet are perfectly aligned—ideal for space-constrained installations in agriculture, municipal, and industrial applications. Perfect for fertigation skids, booster stations, process water systems, and retrofit situations where piping alignment is critical. Manual cleaning with serviceable gasketed heads provides reliable filtration without the footprint of larger filter platforms.
                  </p>
                </div>
              </motion.div>
            </div>

            {/* Right Rail - Downloads & CTAs */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-lg shadow-xl p-6 space-y-4 sticky top-6">
                <h3 className="text-xl font-bold text-gray-900 mb-4">Resources & Support</h3>

                <Link
                  to="/contact?topic=filtration"
                  className="block w-full bg-gray-700 text-white px-6 py-4 rounded-lg font-semibold hover:bg-gray-800 transition-colors text-center shadow-lg"
                >
                  <Mail className="inline-block mr-2 mb-1" size={20} />
                  Talk to a Filtration Specialist
                </Link>

                <div className="space-y-2 pt-4 border-t border-gray-200">
                  <h4 className="font-semibold text-gray-900 text-sm mb-2">Download Resources</h4>

                  <a
                    href="#"
                    className="flex items-center text-sm text-gray-700 hover:text-gray-900 p-2 rounded hover:bg-gray-50 transition-colors"
                    onClick={(e) => e.preventDefault()}
                  >
                    <Download size={16} className="mr-2 flex-shrink-0" />
                    <span>TIG Series Sell Sheet (PDF)</span>
                  </a>

                  <a
                    href="#"
                    className="flex items-center text-sm text-gray-700 hover:text-gray-900 p-2 rounded hover:bg-gray-50 transition-colors"
                    onClick={(e) => e.preventDefault()}
                  >
                    <Download size={16} className="mr-2 flex-shrink-0" />
                    <span>Dimensional Drawing (PDF)</span>
                  </a>

                  <a
                    href="#"
                    className="flex items-center text-sm text-gray-700 hover:text-gray-900 p-2 rounded hover:bg-gray-50 transition-colors"
                    onClick={(e) => e.preventDefault()}
                  >
                    <Download size={16} className="mr-2 flex-shrink-0" />
                    <span>Spec Table (CSV)</span>
                  </a>
                </div>

                <div className="pt-4 border-t border-gray-200">
                  <div className="flex items-center space-x-3 text-gray-700 mb-3">
                    <Phone size={20} />
                    <span className="text-sm">(509) 901-7476</span>
                  </div>
                  <div className="flex items-center space-x-3 text-gray-700">
                    <Mail size={20} />
                    <a href="mailto:Andre@ctcwaterworks.com" className="text-sm hover:text-gray-900 underline">
                      Andre@ctcwaterworks.com
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* TIG Cleaning Exclusion Notice */}
      <section className="py-6 bg-orange-50 border-y border-orange-200">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-start gap-4">
            <AlertTriangle className="text-orange-600 flex-shrink-0 mt-1" size={28} />
            <div>
              <h3 className="font-bold text-gray-900 mb-2">TIG Cleaning & Maintenance Note</h3>
              <p className="text-gray-700 leading-relaxed">
                TIG filters are not compatible with Brush/Vacuum/RAB cleaning options. For TIG maintenance, follow the procedures on this page. TIG filters use manual disassembly for cleaning — the compact inline design does not accommodate brush or vacuum automation assemblies.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Value Props Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">Why Choose TIG Series?</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-gradient-to-br from-gray-50 to-white border border-gray-300 rounded-lg p-6">
              <Minimize2 className="text-gray-700 mb-4" size={36} />
              <h3 className="font-bold text-gray-900 mb-2">True-Inline Geometry</h3>
              <p className="text-gray-700 text-sm leading-relaxed">
                Inlet and outlet perfectly aligned for space-constrained layouts, retrofit installations, and situations where piping geometry is critical.
              </p>
            </div>

            <div className="bg-gradient-to-br from-blue-50 to-white border border-blue-200 rounded-lg p-6">
              <Settings className="text-ctc-blue mb-4" size={36} />
              <h3 className="font-bold text-gray-900 mb-2">Serviceable Design</h3>
              <p className="text-gray-700 text-sm leading-relaxed">
                Gasketed head design allows field access to screens for cleaning or replacement. Standard gaskets and seals are readily available and easy to install.
              </p>
            </div>

            <div className="bg-gradient-to-br from-green-50 to-white border border-green-200 rounded-lg p-6">
              <Filter className="text-green-600 mb-4" size={36} />
              <h3 className="font-bold text-gray-900 mb-2">Compatible Screen Options</h3>
              <p className="text-gray-700 text-sm leading-relaxed">
                Available in 20-120 mesh and 1/32" to 1/8" perforations. Screens are interchangeable—upgrade or change mesh in the field without repiping.
              </p>
            </div>

            <div className="bg-gradient-to-br from-orange-50 to-white border border-orange-200 rounded-lg p-6">
              <CheckCircle className="text-orange-600 mb-4" size={36} />
              <h3 className="font-bold text-gray-900 mb-2">Intake Pairing</h3>
              <p className="text-gray-700 text-sm leading-relaxed">
                Pair with CTC suction screens (SCS2/PSF) for upstream debris control, then polish with TIG inline filters for complete emitter protection.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Specifications Table */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">TIG Series (True Inline Gasketed) — Specifications</h2>
          <p className="text-center text-gray-600 mb-8 max-w-3xl mx-auto">
            Complete technical specifications for TIG true inline gasketed filters. All models feature manual cleaning—no automatic options available.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
            <div className="bg-white rounded-lg shadow-lg overflow-hidden">
              <img src="/TIG.jpeg" alt="TIG Series True Inline Gasketed Filter" className="w-full h-auto" />
              <div className="p-4">
                <p className="text-sm text-gray-700 text-center">Compact true-inline design with quick-access end caps for manual cleaning.</p>
              </div>
            </div>
            <div className="bg-white rounded-lg shadow-lg overflow-hidden">
              <img src="/TIG 1.jpeg" alt="TIG Series Installation View" className="w-full h-auto" />
              <div className="p-4">
                <p className="text-sm text-gray-700 text-center">True inline geometry ideal for space-constrained installations.</p>
              </div>
            </div>
          </div>

          <div className="overflow-x-auto bg-white rounded-lg shadow-lg">
            <table className="w-full border-collapse">
              <thead className="bg-gradient-to-r from-gray-700 to-gray-800 text-white">
                <tr>
                  <th className="px-4 py-3 text-left font-semibold">Item #</th>
                  <th className="px-4 py-3 text-left font-semibold">Connection</th>
                  <th className="px-4 py-3 text-left font-semibold">Size Label</th>
                  <th className="px-4 py-3 text-left font-semibold">Flow (GPM)</th>
                  <th className="px-4 py-3 text-left font-semibold">A (in)</th>
                  <th className="px-4 py-3 text-left font-semibold">B (in)</th>
                  <th className="px-4 py-3 text-left font-semibold">C (in)</th>
                  <th className="px-4 py-3 text-left font-semibold">D (in)</th>
                  <th className="px-4 py-3 text-left font-semibold">Screen Length (in)</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                <tr className="hover:bg-gray-50">
                  <td className="px-4 py-3 font-semibold">BA578</td>
                  <td className="px-4 py-3">3 NPTM</td>
                  <td className="px-4 py-3">3"</td>
                  <td className="px-4 py-3">150–300</td>
                  <td className="px-4 py-3">25¾</td>
                  <td className="px-4 py-3">12</td>
                  <td className="px-4 py-3">31½</td>
                  <td className="px-4 py-3">3</td>
                  <td className="px-4 py-3">18</td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-4 py-3 font-semibold">BA583</td>
                  <td className="px-4 py-3">3 FLG</td>
                  <td className="px-4 py-3">3"</td>
                  <td className="px-4 py-3">150–300</td>
                  <td className="px-4 py-3">25¾</td>
                  <td className="px-4 py-3">12</td>
                  <td className="px-4 py-3">31½</td>
                  <td className="px-4 py-3">3</td>
                  <td className="px-4 py-3">18</td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-4 py-3 font-semibold">BA584</td>
                  <td className="px-4 py-3">4 NPTM</td>
                  <td className="px-4 py-3">4"</td>
                  <td className="px-4 py-3">300–450</td>
                  <td className="px-4 py-3">33¾</td>
                  <td className="px-4 py-3">16</td>
                  <td className="px-4 py-3">40</td>
                  <td className="px-4 py-3">3</td>
                  <td className="px-4 py-3">24</td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-4 py-3 font-semibold">BA580</td>
                  <td className="px-4 py-3">4 FLG</td>
                  <td className="px-4 py-3">4"</td>
                  <td className="px-4 py-3">300–450</td>
                  <td className="px-4 py-3">33¾</td>
                  <td className="px-4 py-3">16</td>
                  <td className="px-4 py-3">40</td>
                  <td className="px-4 py-3">3</td>
                  <td className="px-4 py-3">24</td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-4 py-3 font-semibold">BA582</td>
                  <td className="px-4 py-3">6 FLG</td>
                  <td className="px-4 py-3">6"</td>
                  <td className="px-4 py-3">450–800</td>
                  <td className="px-4 py-3">50</td>
                  <td className="px-4 py-3">18</td>
                  <td className="px-4 py-3">57¼</td>
                  <td className="px-4 py-3">3</td>
                  <td className="px-4 py-3">36</td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-4 py-3 font-semibold">BA585</td>
                  <td className="px-4 py-3">8 FLG</td>
                  <td className="px-4 py-3">8"</td>
                  <td className="px-4 py-3">800–1000</td>
                  <td className="px-4 py-3">63⅝</td>
                  <td className="px-4 py-3">22</td>
                  <td className="px-4 py-3">72</td>
                  <td className="px-4 py-3">3</td>
                  <td className="px-4 py-3">48</td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-4 py-3 font-semibold">BA725</td>
                  <td className="px-4 py-3">10 FLG S</td>
                  <td className="px-4 py-3">10" S</td>
                  <td className="px-4 py-3">1000–1500</td>
                  <td className="px-4 py-3">86½</td>
                  <td className="px-4 py-3">24</td>
                  <td className="px-4 py-3">98¾</td>
                  <td className="px-4 py-3">4</td>
                  <td className="px-4 py-3">60</td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-4 py-3 font-semibold">BA726</td>
                  <td className="px-4 py-3">10 FLG M</td>
                  <td className="px-4 py-3">10" M</td>
                  <td className="px-4 py-3">1500–2500</td>
                  <td className="px-4 py-3">122½</td>
                  <td className="px-4 py-3">24</td>
                  <td className="px-4 py-3">128¾</td>
                  <td className="px-4 py-3">4</td>
                  <td className="px-4 py-3">90</td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-4 py-3 font-semibold">BA794</td>
                  <td className="px-4 py-3">10 FLG L</td>
                  <td className="px-4 py-3">10" L</td>
                  <td className="px-4 py-3">2500–3000</td>
                  <td className="px-4 py-3">152½</td>
                  <td className="px-4 py-3">24</td>
                  <td className="px-4 py-3">158¾</td>
                  <td className="px-4 py-3">4</td>
                  <td className="px-4 py-3">120</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="mt-6 bg-blue-50 border-l-4 border-gray-700 p-6 rounded-r-lg max-w-4xl mx-auto">
            <p className="text-gray-700 leading-relaxed">
              <span className="font-semibold text-gray-900">Important:</span> Flow ranges are guidelines based on typical installations with moderate debris loading. Actual capacity depends on screen mesh size, water quality, and acceptable head loss. TIG filters are manual cleaning only—for automatic options, see BIG Series with brush or vacuum technology. Contact engineering for application-specific sizing.
            </p>
          </div>
        </div>
      </section>

      {/* When to Choose TIG */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">When to Choose TIG Series</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-gradient-to-br from-green-50 to-white border-2 border-green-300 rounded-lg p-8">
              <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                <CheckCircle className="mr-3 text-green-600" size={28} />
                Ideal Applications
              </h3>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <CheckCircle className="mr-2 text-green-600 mt-1 flex-shrink-0" size={18} />
                  <span className="text-gray-700">Space-constrained installations requiring compact footprint</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="mr-2 text-green-600 mt-1 flex-shrink-0" size={18} />
                  <span className="text-gray-700">Fertigation skids and booster stations with tight piping layouts</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="mr-2 text-green-600 mt-1 flex-shrink-0" size={18} />
                  <span className="text-gray-700">Retrofit situations where true-inline geometry is required</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="mr-2 text-green-600 mt-1 flex-shrink-0" size={18} />
                  <span className="text-gray-700">Secondary polishing downstream of suction screens</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="mr-2 text-green-600 mt-1 flex-shrink-0" size={18} />
                  <span className="text-gray-700">Clean water applications with moderate debris loading</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="mr-2 text-green-600 mt-1 flex-shrink-0" size={18} />
                  <span className="text-gray-700">Sites with accessible location for manual cleaning</span>
                </li>
              </ul>
            </div>

            <div className="bg-gradient-to-br from-orange-50 to-white border-2 border-orange-300 rounded-lg p-8">
              <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                <Settings className="mr-3 text-orange-600" size={28} />
                Consider BIG Series Instead
              </h3>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <ArrowRight className="mr-2 text-orange-600 mt-1 flex-shrink-0" size={18} />
                  <span className="text-gray-700">When automatic cleaning is required (brush or vacuum)</span>
                </li>
                <li className="flex items-start">
                  <ArrowRight className="mr-2 text-orange-600 mt-1 flex-shrink-0" size={18} />
                  <span className="text-gray-700">High debris loading requiring frequent cleaning cycles</span>
                </li>
                <li className="flex items-start">
                  <ArrowRight className="mr-2 text-orange-600 mt-1 flex-shrink-0" size={18} />
                  <span className="text-gray-700">Remote installations without regular access</span>
                </li>
                <li className="flex items-start">
                  <ArrowRight className="mr-2 text-orange-600 mt-1 flex-shrink-0" size={18} />
                  <span className="text-gray-700">Applications needing larger screen surface area</span>
                </li>
                <li className="flex items-start">
                  <ArrowRight className="mr-2 text-orange-600 mt-1 flex-shrink-0" size={18} />
                  <span className="text-gray-700">When future retrofit to automatic cleaning is anticipated</span>
                </li>
                <li className="flex items-start">
                  <ArrowRight className="mr-2 text-orange-600 mt-1 flex-shrink-0" size={18} />
                  <span className="text-gray-700">Sizes larger than 6" (BIG available to 8" and beyond)</span>
                </li>
              </ul>
            </div>
          </div>

          <div className="mt-8 bg-blue-50 border-l-4 border-ctc-blue p-6 rounded-r-lg max-w-4xl mx-auto">
            <p className="text-gray-700 leading-relaxed">
              <span className="font-semibold text-gray-900">Decision Guidance:</span> TIG filters excel in space-constrained manual cleaning applications. BIG filters offer automatic cleaning options, larger capacity, and retrofit flexibility. Contact engineering to determine the best platform for your specific layout, water quality, and operational requirements.
            </p>
          </div>
        </div>
      </section>

      {/* Replacement Parts Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Replacement Parts & Service Components</h2>

          <div className="bg-white rounded-lg shadow-lg overflow-hidden mb-8">
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead className="bg-gradient-to-r from-gray-700 to-gray-800 text-white">
                  <tr>
                    <th className="px-4 py-3 text-left font-semibold">Part Category</th>
                    <th className="px-4 py-3 text-left font-semibold">Fitment (models/sizes)</th>
                    <th className="px-4 py-3 text-left font-semibold">Part #</th>
                    <th className="px-4 py-3 text-left font-semibold">Notes</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  <tr className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-semibold">Filter Heads</td>
                    <td className="px-4 py-3 text-sm">Size-specific (2", 3", 4", 6")</td>
                    <td className="px-4 py-3 text-sm">Contact dealer</td>
                    <td className="px-4 py-3 text-sm">Complete head assembly with hardware</td>
                  </tr>
                  <tr className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-semibold">Filter Bodies</td>
                    <td className="px-4 py-3 text-sm">Size-specific (2", 3", 4", 6")</td>
                    <td className="px-4 py-3 text-sm">Contact dealer</td>
                    <td className="px-4 py-3 text-sm">304 SS or epoxy-coated steel</td>
                  </tr>
                  <tr className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-semibold">Head Gaskets</td>
                    <td className="px-4 py-3 text-sm">All TIG models</td>
                    <td className="px-4 py-3 text-sm">Contact dealer</td>
                    <td className="px-4 py-3 text-sm">Replace annually or as needed</td>
                  </tr>
                  <tr className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-semibold">O-Ring Kits</td>
                    <td className="px-4 py-3 text-sm">All TIG models</td>
                    <td className="px-4 py-3 text-sm">Contact dealer</td>
                    <td className="px-4 py-3 text-sm">Complete seal kit for routine maintenance</td>
                  </tr>
                  <tr className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-semibold">Blue Wedges</td>
                    <td className="px-4 py-3 text-sm">All TIG models</td>
                    <td className="px-4 py-3 text-sm">Contact dealer</td>
                    <td className="px-4 py-3 text-sm">Screen retention system</td>
                  </tr>
                  <tr className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-semibold">Replacement Screens</td>
                    <td className="px-4 py-3 text-sm">Size/length/mesh specific</td>
                    <td className="px-4 py-3 text-sm">Contact dealer</td>
                    <td className="px-4 py-3 text-sm">Specify size, length, and mesh/perf</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div className="bg-blue-50 border-l-4 border-ctc-blue p-6 rounded-r-lg max-w-4xl mx-auto">
            <p className="text-gray-700 leading-relaxed">
              <span className="font-semibold text-gray-900">Parts Availability:</span> All replacement parts are stocked by authorized dealers. TIG filters use standard gaskets and screens compatible with other CTC inline filter platforms. For part numbers, availability, and pricing, contact your local dealer or CTC Waterworks engineering. Field service kits available.
            </p>
          </div>
        </div>
      </section>

      {/* Mesh/Perf Quick Guide */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Compatible Screen Options</h2>
          <p className="text-center text-gray-600 mb-8 max-w-3xl mx-auto">
            TIG filters accept standard mesh screens and perforated plates. Choose based on your emitter requirements and water quality.
          </p>

          <div className="bg-white rounded-lg shadow-lg overflow-hidden">
            <table className="w-full">
              <thead className="bg-gradient-to-r from-gray-700 to-gray-800 text-white">
                <tr>
                  <th className="px-6 py-4 text-left font-semibold">Application</th>
                  <th className="px-6 py-4 text-left font-semibold">Recommended Mesh</th>
                  <th className="px-6 py-4 text-left font-semibold">Micron Range</th>
                  <th className="px-6 py-4 text-left font-semibold">Notes</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                <tr className="hover:bg-gray-50">
                  <td className="px-6 py-4 font-semibold text-gray-900">Drip Irrigation</td>
                  <td className="px-6 py-4 text-gray-700">80-120 mesh</td>
                  <td className="px-6 py-4 text-gray-600">125-177 µm</td>
                  <td className="px-6 py-4 text-sm text-gray-600">Fine mesh for emitter protection</td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-6 py-4 font-semibold text-gray-900">Micro-Sprinkler</td>
                  <td className="px-6 py-4 text-gray-700">50-80 mesh</td>
                  <td className="px-6 py-4 text-gray-600">177-297 µm</td>
                  <td className="px-6 py-4 text-sm text-gray-600">Medium mesh, good balance</td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-6 py-4 font-semibold text-gray-900">Intake Polishing</td>
                  <td className="px-6 py-4 text-gray-700">30-50 mesh</td>
                  <td className="px-6 py-4 text-gray-600">297-595 µm</td>
                  <td className="px-6 py-4 text-sm text-gray-600">Secondary filtration after SCS2/PSF</td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-6 py-4 font-semibold text-gray-900">Coarse Pre-Filter</td>
                  <td className="px-6 py-4 text-gray-700">20-30 mesh or 1/16-1/8" perf</td>
                  <td className="px-6 py-4 text-gray-600">595-840 µm</td>
                  <td className="px-6 py-4 text-sm text-gray-600">Upstream protection for equipment</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="mt-6 bg-gray-50 border-l-4 border-gray-700 p-6 rounded-r-lg max-w-4xl mx-auto">
            <p className="text-gray-700 leading-relaxed">
              <span className="font-semibold text-gray-900">Custom Screens:</span> Not limited to standard offerings. Custom mesh sizes and perforation patterns available. TIG screens are interchangeable in the field—change mesh without repiping or welding. Contact engineering for application-specific screen recommendations.
            </p>
          </div>
        </div>
      </section>

      {/* Cross-Links Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">Related Products & Technologies</h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Link
              to="/filtration/inline/big"
              className="bg-white border-2 border-ctc-blue rounded-lg p-6 hover:shadow-xl transition-shadow"
            >
              <h3 className="text-xl font-bold text-gray-900 mb-2">BIG Series</h3>
              <p className="text-gray-700 mb-4 text-sm leading-relaxed">
                Larger capacity inline filters with automatic brush or vacuum cleaning options. Manual or automatic operation, modular platform, retrofit-friendly.
              </p>
              <div className="flex items-center text-ctc-blue font-semibold">
                Compare BIG Series
                <ArrowRight className="ml-2" size={18} />
              </div>
            </Link>

            <Link
              to="/filtration/suction-screens"
              className="bg-white border-2 border-green-300 rounded-lg p-6 hover:shadow-xl transition-shadow"
            >
              <h3 className="text-xl font-bold text-gray-900 mb-2">Intake Protection</h3>
              <p className="text-gray-700 mb-4 text-sm leading-relaxed">
                Self-cleaning suction screens (SCS2) and passive fish-friendly screens (PSF) for upstream debris control before TIG polishing.
              </p>
              <div className="flex items-center text-green-600 font-semibold">
                View Suction Screens
                <ArrowRight className="ml-2" size={18} />
              </div>
            </Link>

            <Link
              to="/filtration/self-cleaning"
              className="bg-white border-2 border-orange-300 rounded-lg p-6 hover:shadow-xl transition-shadow"
            >
              <h3 className="text-xl font-bold text-gray-900 mb-2">Self-Cleaning Tech</h3>
              <p className="text-gray-700 mb-4 text-sm leading-relaxed">
                Learn about automatic brush (RAB) and vacuum (AVC) technologies available on BIG Series for hands-free operation.
              </p>
              <div className="flex items-center text-orange-600 font-semibold">
                View Self-Cleaning Options
                <ArrowRight className="ml-2" size={18} />
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* System Pairing Example */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">System Pairing: Intake + TIG Polishing</h2>
          <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
            Common system configuration combining upstream suction screen with downstream TIG inline filter for complete protection.
          </p>

          <div className="bg-gradient-to-br from-blue-50 to-white border-2 border-blue-300 rounded-lg p-8">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-center">
              <div className="text-center">
                <div className="bg-white rounded-lg shadow-lg p-6 mb-4">
                  <h4 className="font-bold text-gray-900 mb-2">Step 1: Intake Screen</h4>
                  <p className="text-sm text-gray-700">SCS2 or PSF suction screen removes large debris (leaves, twigs, organic matter) at the source</p>
                </div>
                <div className="text-gray-600 text-sm font-semibold">20-30 mesh equivalent</div>
              </div>

              <div className="flex justify-center">
                <ArrowRight className="text-ctc-blue" size={48} />
              </div>

              <div className="text-center">
                <div className="bg-white rounded-lg shadow-lg p-6 mb-4">
                  <h4 className="font-bold text-gray-900 mb-2">Step 2: TIG Polish</h4>
                  <p className="text-sm text-gray-700">TIG inline filter provides fine filtration (80-120 mesh) for emitter protection downstream</p>
                </div>
                <div className="text-gray-600 text-sm font-semibold">80-120 mesh for drip</div>
              </div>
            </div>

            <div className="mt-6 bg-white rounded-lg p-6 border-l-4 border-ctc-blue">
              <p className="text-gray-700 leading-relaxed">
                <span className="font-semibold text-gray-900">System Benefits:</span> Two-stage filtration extends TIG screen life by removing bulk debris upstream. SCS2 handles heavy solids, TIG provides final polishing. Reduces TIG cleaning frequency and ensures consistent fine filtration for emitter protection.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="py-16 bg-gradient-to-br from-gray-700 to-gray-900 text-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl font-bold mb-4">Protect Every Drop — in Any Application</h2>
          <p className="text-xl text-gray-300 mb-6">
            Whether you're safeguarding irrigation lines, pre-treating municipal water, filtering industrial process streams, or protecting hydropower intakes, CTC TIG Series Filters deliver reliable, stainless-steel performance you can trust.
          </p>
          <p className="text-lg text-gray-400 mb-8">
            Talk to a Filtration Specialist today to get a system tailored to your flow rates, solids profile, and operational needs.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact?topic=filtration"
              className="bg-white text-gray-900 px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 transition-colors shadow-lg inline-flex items-center justify-center"
            >
              <Mail className="mr-2" size={20} />
              Talk to a Filtration Specialist
            </Link>

            <Link
              to="/filtration/size-my-filter"
              className="bg-ctc-blue text-white px-8 py-4 rounded-lg font-semibold hover:bg-blue-700 transition-colors shadow-lg inline-flex items-center justify-center"
            >
              <Settings className="mr-2" size={20} />
              Size My Filter
            </Link>
          </div>

          <div className="mt-8 pt-8 border-t border-white/20">
            <div className="flex flex-col sm:flex-row items-center justify-center gap-6 text-gray-300">
              <div className="flex items-center">
                <Phone className="mr-2" size={20} />
                <span>(509) 901-7476</span>
              </div>
              <div className="flex items-center">
                <Mail className="mr-2" size={20} />
                <a href="mailto:Andre@ctcwaterworks.com" className="hover:text-white underline">
                  Andre@ctcwaterworks.com
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default TIGSeriesPage;
